
import React, { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { useParams, useNavigate } from "react-router-dom";
import { type CaixaLotteryType, CAIXA_LOTTERIES } from "@/utils/caixa";
import { type SubscriptionPlan } from "@/utils/predictionUtils";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { generateCaixaPredictions } from "@/utils/caixa";
import LoteriasHeader from "@/components/loterias/LoteriasHeader";
import LoteriasContent from "@/components/loterias/LoteriasContent";
import TrialStatusBanner from "@/components/TrialStatusBanner";
import ActiveTrialBanner from "@/components/ActiveTrialBanner";
import PageInstructions from "@/components/PageInstructions";

const LoteriasPage = () => {
  const { lotteryType } = useParams<{ lotteryType: CaixaLotteryType }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, getTrialInfo } = useAuth();
  const [userPlan, setUserPlan] = useState<SubscriptionPlan>("free");
  const [predictions, setPredictions] = useState<Array<{ value: string; numbers: string[] }>>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedAI, setSelectedAI] = useState<"gemini" | "chatgpt" | "claude">("gemini");
  const [gameCount, setGameCount] = useState(10);
  const [showLastUpdate, setShowLastUpdate] = useState(false);

  const currentLotteryType = lotteryType || "mega-sena";
  const trialInfo = getTrialInfo();
  const isAccessExpired = trialInfo.status === 'trial_expired' || trialInfo.status === 'subscription_expired';

  // Verificar plano do usuário
  useEffect(() => {
    const checkUserPlan = async () => {
      if (!user?.id) return;

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan, created_at, is_subscribed")
          .eq("id", user.id)
          .single();

        if (profile) {
          setUserPlan(profile.plan as SubscriptionPlan || "free");
        }
      } catch (error) {
        console.error("Erro ao verificar plano do usuário:", error);
      }
    };

    if (isAuthenticated && user) {
      checkUserPlan();
    }
  }, [user, isAuthenticated]);

  const handleLotteryChange = (lottery: CaixaLotteryType) => {
    navigate(`/loterias/${lottery}`);
  };

  const handleGeneratePredictions = async () => {
    if (!user) {
      toast({
        title: "Login necessário",
        description: "Você precisa estar logado para gerar palpites.",
        variant: "destructive",
      });
      return;
    }

    // Verificar se o período de teste expirou ou se não tem plano pago
    if (isAccessExpired || userPlan === 'free') {
      toast({
        title: "Acesso restrito",
        description: "Faça upgrade para continuar gerando palpites com IA.",
        variant: "destructive",
      });
      navigate('/planos#escolha-seu-plano');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Simular tempo de processamento
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newPredictions = generateCaixaPredictions(currentLotteryType, gameCount, selectedAI);
      setPredictions(newPredictions);
      setShowLastUpdate(true);
      
      toast({
        title: "Palpites gerados com sucesso!",
        description: `${selectedAI === "gemini" ? "Gemini" : selectedAI === "claude" ? "Claude.ai" : "ChatGPT"} analisou os dados e gerou seus palpites para ${CAIXA_LOTTERIES[currentLotteryType].name}.`,
      });
    } catch (error) {
      toast({
        title: "Erro ao gerar palpites",
        description: "Tente novamente em alguns momentos.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = () => {
    console.log("Compartilhar palpites");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Loterias da Caixa com IA</h1>
            <p className="text-muted-foreground mb-4">
              Você precisa estar logado para acessar os palpites das loterias.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Gerador de Jogos Loterias Caixa com IA - Mega-Sena, Lotofácil, Quina"
        description="Gerador inteligente de jogos para Loterias da Caixa com IA. Palpites para Mega-Sena, Lotofácil, Quina, Lotomania, Timemania, Dupla Sena e mais. Desdobramentos e fechamentos matemáticos."
        keywords="gerador mega-sena, gerador lotofácil, palpites loterias caixa, desdobramento lotofácil, fechamento mega-sena, gerador quina, gerador lotomania, inteligência artificial loterias, palpites com IA"
        canonical="/loterias"
      />
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              🎰 Loterias da Caixa com IA
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Gere jogos inteligentes para as principais loterias do Brasil
            </p>
            
            {/* Trial Status Banner - Moved here */}
            <TrialStatusBanner />
            
            {/* Active Trial Banner */}
            <ActiveTrialBanner />

            <PageInstructions
              title="📖 Como usar as Loterias da Caixa"
              steps={[
                "Escolha a loteria desejada (Mega-Sena, Lotofácil, Quina, etc.)",
                "Selecione qual IA vai gerar os palpites (Gemini, ChatGPT ou Claude)",
                "Defina quantos jogos deseja gerar",
                "Clique em 'Gerar Jogos' e aguarde o processamento",
                "Veja os números gerados — cada jogo é uma combinação diferente pronta para apostar"
              ]}
            />
          </div>

          <LoteriasHeader
            selectedLottery={currentLotteryType}
            onLotteryChange={handleLotteryChange}
            selectedAI={selectedAI}
            onAIChange={setSelectedAI}
            gameCount={gameCount}
            onGameCountChange={setGameCount}
            userPlan={userPlan}
            disabled={isAccessExpired || userPlan === 'free'}
          />

          <LoteriasContent
            lotteryType={currentLotteryType}
            predictions={predictions}
            userPlan={userPlan}
            isGenerating={isGenerating}
            selectedAI={selectedAI}
            gameCount={gameCount}
            showLastUpdate={showLastUpdate}
            setShowLastUpdate={setShowLastUpdate}
            onGeneratePredictions={handleGeneratePredictions}
            onShare={handleShare}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default LoteriasPage;
